import { mediaUrl, coverForSlug } from './media'

const LOCAL_API_URL = 'http://localhost:8787'
const PROD_API_URL = 'https://api.fastingcarnivore.com'
const IS_LOCAL_DEV =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

export const API_URL = import.meta.env.VITE_API_URL || (IS_LOCAL_DEV ? LOCAL_API_URL : PROD_API_URL)

function getToken(): string | null {
  return localStorage.getItem('fc_session_token')
}

function setToken(token: string): void {
  localStorage.setItem('fc_session_token', token)
}

function clearToken(): void {
  localStorage.removeItem('fc_session_token')
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(body.error || `Request failed: ${res.status}`)
  }

  const json = await res.json()
  return json.data !== undefined ? json.data : json
}

// Auth
export const auth = {
  sendMagicLink: (email: string, firstName?: string) =>
    request<{ success: boolean }>('/api/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email, firstName }),
    }),

  verify: async (token: string) => {
    const data = await request<{ token: string; user: AuthUser }>(
      `/api/auth/verify?token=${encodeURIComponent(token)}`
    )
    setToken(data.token)
    return data
  },

  me: () => request<AuthUser | null>('/api/auth/me'),

  updateProfile: (params: { name?: string }) =>
    request<{ success: boolean }>('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(params),
    }),

  logout: async () => {
    await request('/api/auth/logout', { method: 'POST' }).catch(() => {})
    clearToken()
  },

  // Passkey methods removed — magic link only
}

// Books
export const books = {
  list: async () => {
    const items = await request<any[]>('/api/books')
    return (items ?? []).map((b) => ({
      ...b,
      coverUrl: b.coverUrl ? mediaUrl(b.coverUrl) : b.cover_url ? mediaUrl(b.cover_url) : coverForSlug(b.slug),
    })) as BookListItem[]
  },

  getBySlug: async (slug: string) => {
    const b = await request<any>(`/api/books/${encodeURIComponent(slug)}`)
    if (!b) return b
    return {
      ...b,
      coverUrl: b.coverUrl ? mediaUrl(b.coverUrl) : b.cover_url ? mediaUrl(b.cover_url) : coverForSlug(b.slug),
    } as BookDetail
  },

  trackDownload: (materialId: string) =>
    request('/api/books/track-download', {
      method: 'POST',
      body: JSON.stringify({ materialId }),
    }),
}

// Reader
export const reader = {
  getPosition: (bookId: string) =>
    request<ReadingPosition | null>(`/api/reader/position/${bookId}`),

  updatePosition: (bookId: string, chapter: number, scrollPosition: number) =>
    request('/api/reader/position/' + bookId, {
      method: 'PUT',
      body: JSON.stringify({ chapterNumber: chapter, scrollPosition }),
    }),

  getHighlights: (chapterId: string) =>
    request<HighlightData[]>(`/api/reader/highlights/${chapterId}`),

  createHighlight: (params: CreateHighlightParams) =>
    request<{ highlightId: string }>('/api/reader/highlights', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  updateHighlight: (id: string, updates: { color?: string; note?: string }) =>
    request(`/api/reader/highlights/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  deleteHighlight: (id: string) =>
    request(`/api/reader/highlights/${id}`, { method: 'DELETE' }),

  getNote: (chapterId: string) =>
    request<ChapterNoteData | null>(`/api/reader/notes/${chapterId}`),

  upsertNote: (params: UpsertNoteParams) =>
    request('/api/reader/notes', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  deleteNote: (noteId: string) =>
    request(`/api/reader/notes/${noteId}`, { method: 'DELETE' }),
}

// Discussions
export const discussions = {
  getChapter: (chapterId: string) =>
    request<DiscussionData[]>(`/api/discussions/${chapterId}`),

  post: (chapterId: string, content: string) =>
    request('/api/discussions', {
      method: 'POST',
      body: JSON.stringify({ chapterId, content }),
    }),
}

// Workbook
export const workbook = {
  get: (materialId: string) =>
    request<WorkbookNoteData | null>(`/api/workbook/${materialId}`),

  upsert: (params: { materialId: string; bookId?: string; content: string; images: string[]; drawings?: string }) =>
    request('/api/workbook', {
      method: 'PUT',
      body: JSON.stringify(params),
    }),
}

// Dashboard (admin)
export const dashboard = {
  analytics: () => request<DashboardAnalytics>('/api/dashboard/analytics'),
  books: () => request<AdminBook[]>('/api/dashboard/books'),
  materials: () => request<AdminMaterial[]>('/api/dashboard/materials'),
  members: () => request<AdminMember[]>('/api/dashboard/members'),
}

// Billing (Stripe Checkout)
export const billing = {
  checkout: (productSlug?: string) =>
    request<{ checkoutUrl: string }>('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ productSlug }),
    }),
  publicCheckout: (email: string, affiliateCode?: string, productSlug?: string) =>
    request<{ checkoutUrl: string }>('/api/billing/checkout/public', {
      method: 'POST',
      body: JSON.stringify({ email, affiliateCode, productSlug }),
    }),
  confirm: (sessionId: string) =>
    request<{ success: boolean }>('/api/billing/confirm', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    }),
}

// Affiliates
export const affiliates = {
  list: () => request<AffiliateData[]>('/api/affiliates'),
  create: (params: CreateAffiliateParams) =>
    request('/api/affiliates', { method: 'POST', body: JSON.stringify(params) }),
  update: (id: string, params: UpdateAffiliateParams) =>
    request(`/api/affiliates/${id}`, { method: 'PUT', body: JSON.stringify(params) }),
  track: (code: string, visitorId: string) =>
    request('/api/affiliates/track', { method: 'POST', body: JSON.stringify({ code, visitorId }) }),
  myData: () => request<MyAffiliateData | null>('/api/affiliates?scope=me'),
  stats: () => request<AffiliateStats>('/api/affiliates/stats'),
  byCode: (code: string) => request<AffiliatePublic | null>(`/api/affiliates/by-code/${code}`),
  requestPayout: () => request('/api/affiliates/payout', { method: 'POST' }),
}

// Discord
export const discord = {
  status: () => request<DiscordStatus>('/api/discord/status'),
  verify: (code: string) => request('/api/discord/verify', { method: 'POST', body: JSON.stringify({ code }) }),
  unlink: () => request('/api/discord/unlink', { method: 'POST' }),
  recovery: (discordUserId: string) =>
    request('/api/discord/recovery', { method: 'POST', body: JSON.stringify({ discordUserId }) }),
  generateVerificationCode: () =>
    request<{ code: string }>('/api/discord/generate-code', { method: 'POST' }),
  verifyRecoveryCode: (code: string) =>
    request<{ success: boolean; token?: string; user?: AuthUser; needsNewPasskey?: boolean }>('/api/discord/verify-recovery', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
}

// Passkeys — removed, magic link only

// Compliance
export const compliance = {
  exportData: () => request<UserDataExport>('/api/compliance/export'),
  deleteAccount: () => request('/api/compliance/account', { method: 'DELETE' }),
}

// Admin
export const admin = {
  customers: () => request<AdminCustomer[]>('/api/admin/customers'),
  stats: () => request<AdminStats>('/api/admin/stats'),
  broadcast: (channel: string, message: string) =>
    request('/api/admin/broadcast', { method: 'POST', body: JSON.stringify({ channel, message }) }),
  broadcastHistory: (limit?: number) =>
    request<any[]>(`/api/admin/broadcast/history${limit ? `?limit=${limit}` : ''}`),
  agents: () => request<AgentInfo[]>('/api/admin/agents'),
  agentLog: (limit?: number) =>
    request<AgentLogEntry[]>(`/api/admin/agent-log${limit ? `?limit=${limit}` : ''}`),
  insights: (status?: string) =>
    request<InsightItem[]>(`/api/admin/insights${status ? `?status=${status}` : ''}`),
  approveInsight: (id: string) =>
    request('/api/admin/insights/' + id + '/approve', { method: 'POST' }),
  rejectInsight: (id: string) =>
    request('/api/admin/insights/' + id + '/reject', { method: 'POST' }),
}

// Admin book/material management
export const adminBooks = {
  create: (params: { title: string; subtitle?: string; slug: string }) =>
    request<{ id: string }>('/api/dashboard/books', { method: 'POST', body: JSON.stringify(params) }),
  delete: (bookId: string) =>
    request(`/api/dashboard/books/${bookId}`, { method: 'DELETE' }),
  setVisibility: (bookId: string, status: string) =>
    request(`/api/dashboard/books/${bookId}/visibility`, { method: 'PUT', body: JSON.stringify({ status }) }),
  uploadCover: (bookId: string, storageId: string) =>
    request(`/api/dashboard/books/${bookId}/cover`, { method: 'PUT', body: JSON.stringify({ storageId }) }),
  createFromManuscript: (params: { title: string; subtitle?: string; slug: string; coverStorageId?: string; chapters: any[] }) =>
    request<{ id: string }>('/api/dashboard/books/from-manuscript', { method: 'POST', body: JSON.stringify(params) }),
  addChapter: (bookId: string, params: { title: string; content: string; isFreePreview?: boolean }) =>
    request<{ id: string }>(`/api/dashboard/books/${bookId}/chapters`, { method: 'POST', body: JSON.stringify(params) }),
  updateChapter: (chapterId: string, params: { title?: string; content?: string; isFreePreview?: boolean }) =>
    request(`/api/dashboard/chapters/${chapterId}`, { method: 'PUT', body: JSON.stringify(params) }),
  deleteChapter: (chapterId: string) =>
    request(`/api/dashboard/chapters/${chapterId}`, { method: 'DELETE' }),
}

export const adminMaterials = {
  create: (params: { name: string; description?: string; type: string; fileUrl?: string; bookId?: string; storageId?: string; fileSize?: string }) =>
    request<{ id: string }>('/api/dashboard/materials', { method: 'POST', body: JSON.stringify(params) }),
  delete: (materialId: string) =>
    request(`/api/dashboard/materials/${materialId}`, { method: 'DELETE' }),
  upload: (params: { name: string; description?: string; type: string; bookId?: string; storageId: string; fileSize: string }) =>
    request<{ id: string }>('/api/dashboard/materials/upload', { method: 'POST', body: JSON.stringify(params) }),
}

export const uploads = {
  generateUploadUrl: () =>
    request<{ url: string }>('/api/uploads/generate-url', { method: 'POST' }),
  parseManuscript: (storageId: string, fileType: string) =>
    request<{ title: string; chapters: any[] } | { error: string }>('/api/uploads/parse-manuscript', {
      method: 'POST',
      body: JSON.stringify({ storageId, fileType }),
    }),
}

// Audio (TTS)
export const audioApi = {
  getChapterAudio: (chapterId: string) =>
    request<{ chapterId: string; audioUrl: string | null; hasAudio: boolean }>(
      `/api/audio/chapters/${chapterId}`
    ),

  generate: (chapterId: string, voiceId?: string) =>
    request<{ chapterId: string; audioUrl: string; sizeBytes: number }>(
      '/api/audio/generate',
      { method: 'POST', body: JSON.stringify({ chapterId, voiceId }) }
    ),

  status: () =>
    request<{ id: string; title: string; number: number; bookTitle: string; bookSlug: string; hasAudio: boolean; audioUrl: string | null }[]>(
      '/api/audio/status'
    ),
}

// Analytics
export const analytics = {
  revenue: () => request<any>('/api/analytics/revenue'),
  engagement: () => request<any>('/api/analytics/engagement'),
  content: () => request<any>('/api/analytics/content'),
}

// Types
export interface AuthUser {
  id: string
  email: string
  name: string | null
  isAdmin: boolean
  isStaff: boolean
  hasPurchased: boolean
}

export interface BookListItem {
  id: string
  title: string
  subtitle?: string
  slug: string
  description?: string
  coverUrl?: string
  order: number
  progress?: { currentChapter: number; completedChapters: number[]; isCompleted: boolean }
}

export interface BookDetail {
  id: string
  title: string
  subtitle?: string
  slug: string
  description?: string
  coverUrl?: string
  chapters: ChapterData[]
  materials: MaterialData[]
}

export interface ChapterData {
  id: string
  number: number
  title: string
  content: string
  isFreePreview: boolean
}

export interface MaterialData {
  id: string
  name: string
  description?: string
  type: string
  fileUrl: string
  fileSize?: string
  downloadCount: number
}

export interface ReadingPosition {
  currentChapter: number
  scrollPosition: number
}

export interface HighlightData {
  id: string
  chapterNumber: number
  selectedText: string
  startOffset: number
  endOffset: number
  color: string
  note?: string
  createdAt: number
}

export interface CreateHighlightParams {
  bookId: string
  chapterId: string
  chapterNumber: number
  selectedText: string
  startOffset: number
  endOffset: number
  color: string
}

export interface ChapterNoteData {
  id: string
  content: string
  updatedAt: number
}

export interface UpsertNoteParams {
  bookId: string
  chapterId: string
  chapterNumber: number
  content: string
}

export interface DiscussionData {
  id: string
  userName: string
  userAvatar?: string
  content: string
  source: 'app' | 'discord'
  createdAt: number
}

export interface WorkbookNoteData {
  id: string
  content: string
  images: string[]
  drawings?: string
  updatedAt: number
}

export interface DashboardAnalytics {
  revenue: { total: number; monthly: number; avgOrder: number; ltv: number }
  engagement: { activeUsers7d: number; activeUsers30d: number; discordLinkRate: number }
  content: { totalBooks: number; totalChapters: number; totalMaterials: number }
  growth: { totalMembers: number; dailySignups: number; growthRate: number }
}

export interface AdminBook {
  id: string; title: string; slug: string; status: string; chapterCount: number; order: number
}

export interface AdminMaterial {
  id: string; name: string; type: string; downloadCount: number; status: string
}

export interface AdminMember {
  id: string; email: string; name?: string; hasPurchased: boolean; createdAt: number; lastLoginAt?: number
}

export interface AffiliateData {
  id: string; code: string; customSlug?: string; commissionRate: number; status: string
  totalEarnings: number; pendingPayout: number; totalReferrals: number
  user?: { id: string; email: string; name?: string }
}

export interface CreateAffiliateParams {
  userId: string; code?: string; customSlug?: string; commissionRate?: number; paypalEmail?: string
}

export interface UpdateAffiliateParams {
  code?: string; customSlug?: string; commissionRate?: number; status?: string; paypalEmail?: string
}

export interface MyAffiliateData {
  affiliate: AffiliateData
  stats: { totalClicks: number; signups: number; conversions: number; conversionRate: number }
  recentReferrals: { id: string; status: string; clickedAt: number; commissionAmount?: number }[]
  payoutHistory: { id: string; amount: number; status: string; createdAt: number }[]
}

export interface AffiliateStats {
  totalAffiliates: number; activeAffiliates: number; totalEarnings: number
  pendingPayouts: number; totalClicks: number; totalConversions: number; conversionRate: number
}

export interface AffiliatePublic { id: string; code: string; customSlug?: string }

export interface DiscordStatus { linked: boolean; username?: string; userId?: string }

// PasskeyInfo removed — magic link only

export interface UserDataExport { user: AuthUser; highlights: HighlightData[]; notes: ChapterNoteData[] }

export interface AdminCustomer {
  id: string; email: string; name?: string; hasPurchased: boolean; purchasedAt?: number
  totalSpent: number; discordLinked: boolean
}

export interface AdminStats {
  totalMembers: number; paidMembers: number; revenue: number; activeReaders: number
}

export interface AgentInfo {
  id: string; name: string; status: 'active' | 'dormant' | 'disabled'; lastRunAt?: number
  description?: string; successRate?: number
}

export interface AgentLogEntry {
  id: string; agentName: string; action: string; status: string; createdAt: number
  details?: string
}

export interface InsightItem {
  id: string; agentName: string; type: string; title: string; content: string
  status: 'pending' | 'approved' | 'rejected'; createdAt: number
}
